import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export type OfflineModeStateValue = 'on' | 'off';

export type OfflineModeDirection = 'activate' | 'return';

export type OfflineModeActor = {
  token: string;
  name?: string;
};

export type OfflineModeDivergence = {
  reason: string;
  detectedAt?: number;
  lastSeenAt?: number;
  peer?: {
    version?: number;
    state?: string;
    epoch?: string;
  };
};

export type OfflineModeActivation = {
  orgToken: string;
  state: OfflineModeStateValue;
  version: number;
  origin: string;
  updatedAt: number;
  actor?: OfflineModeActor;
  publishedVersion?: number;
  publishedAt?: number;
  divergence?: OfflineModeDivergence;
};

export type OfflineModeActivationWindow = {
  startedAt: number;
  endedAt: number;
};

export type OfflineModeReachDirection = 'activation' | 'return';

export const reachDirectionFor = (direction: OfflineModeDirection): OfflineModeReachDirection =>
  direction === 'activate' ? 'activation' : 'return';

export type OfflineModeReachField =
  | 'reachable_now'
  | 'reachable_on_rejoin'
  | 'not_reachable'
  | 'total';

export type OfflineModeReachBound = 'at_least' | 'at_most' | 'exact';

export type OfflineModeReachLimit = {
  code: string;
  ticket?: string;
  severity?: string;
  effect?: string;
  affects: string[];
  text?: string;
};

export type OfflineModeReachQuality = {
  level: string;
  denominatorTrustworthy: boolean;
  numeratorTrustworthy: boolean;
  bounds: Partial<Record<OfflineModeReachField, OfflineModeReachBound>>;
  limits: OfflineModeReachLimit[];
  displayCaveat?: string;
  safeDirection?: string;
};

export type OfflineModeReachCounts = {
  reachableNow: number;
  reachableOnRejoin: number;
  notReachable: number;
  total: number;
  outsideRoster?: number;
  willBeInformed?: number;
  label?: string;
};

export type OfflineModeReachRoster = {
  replicatedAt?: number;
  userCount?: number;
  stale?: boolean;
  mode?: string;
};

export type OfflineModeReachChannel = {
  channel: string;
  available: boolean;
  basis: string;
  lastSuccessAt?: number;
  text?: string;
};

export type OfflineModeReach = {
  direction: OfflineModeReachDirection;
  measured: boolean;
  generatedAt?: number;
  counts: OfflineModeReachCounts | null;
  roster: OfflineModeReachRoster | null;
  channels: OfflineModeReachChannel[];
  quality: OfflineModeReachQuality;
  unavailableReason?: string;
  unavailableCode?: string;
  activeSince?: number;
};

export type OfflineModeReachStatus = 'idle' | 'gathering' | 'ready' | 'error';

export type OfflineModeReachErrorCode = 'unreachable' | 'forbidden' | 'read-failed' | 'unusable';

export type OfflineModeReachError = {
  code: OfflineModeReachErrorCode;
  serverMessage?: string;
};

type OfflineModeReachFailure = OfflineModeReachError & { direction: OfflineModeReachDirection };

export const OFFLINE_MODE_EVENT_TYPES = [
  'activation',
  'deactivation',
  'reach_unavailable',
  'broadcast',
  'care_conduit_routing_change',
  'publication_status',
] as const;

export type OfflineModeEventSource = {
  node?: string;
  side?: string;
  producer?: string;
};

export type OfflineModeEventDetail = {
  state?: string;
  version?: number;
  origin?: string;
  phase?: string;
  reason?: string;
  body?: string;
  recipientScope?: {
    kind?: string;
    orgToken?: string;
    resolvedCount?: number;
  };
  template?: string;
  direction?: string;
  path?: string;
  previousPath?: string;
  status?: string;
  publishedVersion?: number;
  target?: string;
  note?: string;
};

export type OfflineModeEvent = {
  seq: number;
  eventId?: string;
  eventType: string;
  occurredAt: number;
  recordedAt?: number;
  actor?: OfflineModeActor;
  actorKnown: boolean;
  source: OfflineModeEventSource;
  detail: OfflineModeEventDetail;
  annotations: string[];
  prevHash?: string;
  hash?: string;
};

export type OfflineModeEventChainFinding = {
  type: string;
  seq?: number;
};

export type OfflineModeEventChain = {
  scope?: string;
  checked: number;
  intact: boolean;
  anchored: boolean;
  findings: OfflineModeEventChainFinding[];
};

export type OfflineModeEventRetention = {
  days?: number;
  configKey?: string;
  status?: string;
  export?: string;
  note?: string;
};

export type OfflineModeEventPage = {
  events: OfflineModeEvent[];
  chain: OfflineModeEventChain | null;
  retention: OfflineModeEventRetention | null;
  annotationText: Record<string, string>;
  nextPage: string | null;
  isFullyFetched: boolean;
  unreadable: number;
  append: boolean;
};

export type OfflineModeEventLogStatus = 'idle' | 'loading' | 'loading-more' | 'ready' | 'error';

export type OfflineModeEventLogErrorCode = 'unreachable' | 'forbidden' | 'read-failed' | 'unusable';

export type OfflineModeEventLogError = {
  code: OfflineModeEventLogErrorCode;
  serverMessage?: string;
};

export type OfflineModeBroadcastDirection = 'to_offline' | 'to_cloud';

export type OfflineModeBroadcastTemplate = 'activation' | 'return' | 'custom';

export type OfflineModeBroadcastServerTemplate = Exclude<OfflineModeBroadcastTemplate, 'custom'>;

export type OfflineModeBroadcastPrepare = {
  direction: OfflineModeBroadcastDirection;
  defaultTemplate: OfflineModeBroadcastServerTemplate;
  templates: Record<OfflineModeBroadcastServerTemplate, string>;
  recipientScope: { kind: string; orgToken: string; resolvedCount: number };
  reachableNow: { count: number; measuredAt: number } | null;
  limits: { maxBodyBytes: number };
};

export type OfflineModeBroadcastPrepareStatus = 'idle' | 'loading' | 'ready' | 'failed';

export type OfflineModeBroadcastPrepareErrorCode =
  | 'unreachable'
  | 'forbidden'
  | 'read-failed'
  | 'unusable';

export type OfflineModeBroadcastRefusalCode =
  | 'empty_body'
  | 'body_too_large'
  | 'invalid_template'
  | 'no_recipients'
  | 'phi_suspected'
  | 'no_recorded_admin_action'
  | 'forbidden'
  | 'broadcast_rate_limited'
  | 'roster_unavailable'
  | 'not_local_node'
  | 'fanout_failed'
  | 'unreachable'
  | 'send_failed';

export type OfflineModeBroadcastRefusal = {
  code: OfflineModeBroadcastRefusalCode;
  detail?: string;
};

export type OfflineModeBroadcastSendResult = {
  broadcastId: string;
  deliveredNow: number;
  resolvedCount: number;
};

export type OfflineModeBroadcastSendInput = {
  body: string;
  template: OfflineModeBroadcastTemplate;
};

export type OfflineModeLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export type OfflineModeState = {
  organizationId: string | null;
  activation: OfflineModeActivation | null;
  status: OfflineModeLoadStatus;
  isSwitching: boolean;
  writeError: string | null;
  writeErrorDirection: OfflineModeDirection | null;
  isDeploymentUnreachable: boolean;
  lastActivation: OfflineModeActivationWindow | null;
  lastActivationStatus: OfflineModeLoadStatus;
  reach: OfflineModeReach | null;
  reachStatus: OfflineModeReachStatus;
  reachDirection: OfflineModeReachDirection | null;
  reachError: OfflineModeReachError | null;

  events: OfflineModeEvent[];
  eventsStatus: OfflineModeEventLogStatus;
  eventsError: OfflineModeEventLogError | null;
  eventChains: OfflineModeEventChain[];
  eventRetention: OfflineModeEventRetention | null;
  eventAnnotationText: Record<string, string>;
  eventsNextPage: string | null;
  eventsFullyFetched: boolean;
  eventsUnreadable: number;

  broadcastPrepare: OfflineModeBroadcastPrepare | null;
  broadcastPrepareStatus: OfflineModeBroadcastPrepareStatus;
  broadcastPrepareError: OfflineModeBroadcastPrepareErrorCode | null;
  broadcastSending: boolean;
  broadcastRefusal: OfflineModeBroadcastRefusal | null;
  broadcastLastResult: OfflineModeBroadcastSendResult | null;
};

const initialState: OfflineModeState = {
  organizationId: null,
  activation: null,
  status: 'idle',
  isSwitching: false,
  writeError: null,
  writeErrorDirection: null,
  isDeploymentUnreachable: false,
  lastActivation: null,
  lastActivationStatus: 'idle',
  reach: null,
  reachStatus: 'idle',
  reachDirection: null,
  reachError: null,
  events: [],
  eventsStatus: 'idle',
  eventsError: null,
  eventChains: [],
  eventRetention: null,
  eventAnnotationText: {},
  eventsNextPage: null,
  eventsFullyFetched: false,
  eventsUnreadable: 0,
  broadcastPrepare: null,
  broadcastPrepareStatus: 'idle',
  broadcastPrepareError: null,
  broadcastSending: false,
  broadcastRefusal: null,
  broadcastLastResult: null,
};

type OrganizationMeta = { organizationId?: string };

type ForOrganization<P = undefined> = PayloadAction<P, string, OrganizationMeta>;

const forOrganization =
  <P>() =>
  (payload: P, organizationId?: string) => ({ payload, meta: { organizationId } });

const organizationOnly = (organizationId?: string) => ({
  payload: undefined,
  meta: { organizationId },
});

const isForeign = (state: OfflineModeState, { meta }: { meta: OrganizationMeta }): boolean =>
  meta.organizationId !== undefined && meta.organizationId !== state.organizationId;

export const offlineModeSlice = createSlice({
  name: 'offlineMode',
  initialState,
  reducers: {
    setOfflineModeLoading: (state, action: PayloadAction<string | undefined>) => {
      const organizationId = action.payload ?? null;

      if (organizationId !== state.organizationId) {
        state.organizationId = organizationId;
        state.activation = null;
        state.lastActivation = null;
        state.lastActivationStatus = 'idle';
      }

      state.status = 'loading';
    },

    setOfflineModeActivation: {
      reducer: (state, action: ForOrganization<OfflineModeActivation>) => {
        if (isForeign(state, action)) return;
        state.activation = action.payload;
        state.status = 'ready';
        state.isDeploymentUnreachable = false;
      },
      prepare: forOrganization<OfflineModeActivation>(),
    },

    setOfflineModeLoadFailed: {
      reducer: (state, action: ForOrganization) => {
        if (isForeign(state, action)) return;
        state.status = 'error';
      },
      prepare: organizationOnly,
    },

    setOfflineModeSwitching: (state, action: PayloadAction<boolean>) => {
      state.isSwitching = action.payload;
    },

    setOfflineModeWriteError: (
      state,
      action: PayloadAction<{ message: string; direction: OfflineModeDirection }>
    ) => {
      state.writeError = action.payload.message;
      state.writeErrorDirection = action.payload.direction;
    },

    clearOfflineModeWriteError: (state) => {
      state.writeError = null;
      state.writeErrorDirection = null;
    },

    setOfflineModeDeploymentUnreachable: {
      reducer: (state, action: ForOrganization<boolean>) => {
        if (isForeign(state, action)) return;
        state.isDeploymentUnreachable = action.payload;
      },
      prepare: forOrganization<boolean>(),
    },

    setOfflineModeLastActivation: {
      reducer: (state, action: ForOrganization<OfflineModeActivationWindow | null>) => {
        if (isForeign(state, action)) return;
        state.lastActivation = action.payload;
        state.lastActivationStatus = 'ready';
      },
      prepare: forOrganization<OfflineModeActivationWindow | null>(),
    },

    setOfflineModeLastActivationStatus: {
      reducer: (state, action: ForOrganization<OfflineModeLoadStatus>) => {
        if (isForeign(state, action)) return;
        state.lastActivationStatus = action.payload;
      },
      prepare: forOrganization<OfflineModeLoadStatus>(),
    },

    setOfflineModeReachGathering: (state, action: PayloadAction<OfflineModeReachDirection>) => {
      state.reach = null;
      state.reachError = null;
      state.reachStatus = 'gathering';
      state.reachDirection = action.payload;
    },

    setOfflineModeReach: {
      reducer: (state, action: ForOrganization<OfflineModeReach>) => {
        if (isForeign(state, action)) return;
        if (state.reachStatus !== 'gathering') return;
        if (state.reachDirection && state.reachDirection !== action.payload.direction) return;
        state.reach = action.payload;
        state.reachStatus = 'ready';
        state.reachError = null;
      },
      prepare: forOrganization<OfflineModeReach>(),
    },

    setOfflineModeReachFailed: {
      reducer: (state, action: ForOrganization<OfflineModeReachFailure>) => {
        if (isForeign(state, action)) return;
        const { direction, ...error } = action.payload;
        if (state.reachDirection && state.reachDirection !== direction) return;
        state.reach = null;
        state.reachStatus = 'error';
        state.reachError = error;
      },
      prepare: forOrganization<OfflineModeReachFailure>(),
    },

    clearOfflineModeReach: (state) => {
      state.reach = null;
      state.reachStatus = 'idle';
      state.reachDirection = null;
      state.reachError = null;
    },

    setOfflineModeEventsLoading: (state, action: PayloadAction<{ isMore: boolean }>) => {
      state.eventsStatus = action.payload.isMore ? 'loading-more' : 'loading';
      if (!action.payload.isMore) state.eventsError = null;
    },

    setOfflineModeEvents: {
      reducer: (state, action: ForOrganization<OfflineModeEventPage>) => {
        if (isForeign(state, action)) return;
        const page = action.payload;
        const carried = page.append ? state.eventChains : [];

        state.events = page.append ? [...state.events, ...page.events] : page.events;
        state.eventChains = page.chain ? [...carried, page.chain] : carried;
        state.eventsUnreadable = (page.append ? state.eventsUnreadable : 0) + page.unreadable;
        state.eventRetention = page.append
          ? page.retention ?? state.eventRetention
          : page.retention;
        state.eventAnnotationText = page.append
          ? { ...state.eventAnnotationText, ...page.annotationText }
          : page.annotationText;
        state.eventsNextPage = page.nextPage;
        state.eventsFullyFetched = page.isFullyFetched;
        state.eventsStatus = 'ready';
        state.eventsError = null;
      },
      prepare: forOrganization<OfflineModeEventPage>(),
    },

    setOfflineModeEventsFailed: {
      reducer: (state, action: ForOrganization<OfflineModeEventLogError>) => {
        if (isForeign(state, action)) return;
        state.eventsStatus = 'error';
        state.eventsError = action.payload;
      },
      prepare: forOrganization<OfflineModeEventLogError>(),
    },

    clearOfflineModeEvents: (state) => {
      state.events = [];
      state.eventsStatus = 'idle';
      state.eventsError = null;
      state.eventChains = [];
      state.eventRetention = null;
      state.eventAnnotationText = {};
      state.eventsNextPage = null;
      state.eventsFullyFetched = false;
      state.eventsUnreadable = 0;
    },

    setOfflineModeBroadcastPrepare: {
      reducer: (state, action: ForOrganization<OfflineModeBroadcastPrepare | null>) => {
        if (isForeign(state, action)) return;
        state.broadcastPrepare = action.payload;
      },
      prepare: forOrganization<OfflineModeBroadcastPrepare | null>(),
    },

    setOfflineModeBroadcastPrepareStatus: {
      reducer: (state, action: ForOrganization<OfflineModeBroadcastPrepareStatus>) => {
        if (isForeign(state, action)) return;
        state.broadcastPrepareStatus = action.payload;
      },
      prepare: forOrganization<OfflineModeBroadcastPrepareStatus>(),
    },

    setOfflineModeBroadcastPrepareError: {
      reducer: (state, action: ForOrganization<OfflineModeBroadcastPrepareErrorCode | null>) => {
        if (isForeign(state, action)) return;
        state.broadcastPrepareError = action.payload;
      },
      prepare: forOrganization<OfflineModeBroadcastPrepareErrorCode | null>(),
    },

    setOfflineModeBroadcastSending: (state, action: PayloadAction<boolean>) => {
      state.broadcastSending = action.payload;
    },

    setOfflineModeBroadcastRefusal: {
      reducer: (state, action: ForOrganization<OfflineModeBroadcastRefusal | null>) => {
        if (isForeign(state, action)) return;
        state.broadcastRefusal = action.payload;
      },
      prepare: forOrganization<OfflineModeBroadcastRefusal | null>(),
    },

    setOfflineModeBroadcastResult: {
      reducer: (state, action: ForOrganization<OfflineModeBroadcastSendResult | null>) => {
        if (isForeign(state, action)) return;
        state.broadcastLastResult = action.payload;
      },
      prepare: forOrganization<OfflineModeBroadcastSendResult | null>(),
    },

    clearOfflineModeBroadcast: (state) => {
      state.broadcastPrepare = null;
      state.broadcastPrepareStatus = 'idle';
      state.broadcastPrepareError = null;
      state.broadcastSending = false;
      state.broadcastRefusal = null;
      state.broadcastLastResult = null;
    },

    resetOfflineMode: () => initialState,
  },
});

export const {
  clearOfflineModeBroadcast,
  clearOfflineModeEvents,
  clearOfflineModeReach,
  clearOfflineModeWriteError,
  resetOfflineMode,
  setOfflineModeActivation,
  setOfflineModeBroadcastPrepare,
  setOfflineModeBroadcastPrepareError,
  setOfflineModeBroadcastPrepareStatus,
  setOfflineModeBroadcastRefusal,
  setOfflineModeBroadcastResult,
  setOfflineModeBroadcastSending,
  setOfflineModeDeploymentUnreachable,
  setOfflineModeEvents,
  setOfflineModeEventsFailed,
  setOfflineModeEventsLoading,
  setOfflineModeLastActivation,
  setOfflineModeLastActivationStatus,
  setOfflineModeLoadFailed,
  setOfflineModeLoading,
  setOfflineModeReach,
  setOfflineModeReachFailed,
  setOfflineModeReachGathering,
  setOfflineModeSwitching,
  setOfflineModeWriteError,
} = offlineModeSlice.actions;

export const isOfflineModeActive = (state: OfflineModeState): boolean =>
  state.activation?.state === 'on';

export default offlineModeSlice;
